/* eslint-disable no-underscore-dangle */
import { ActionIcon, Box, Loader, Text, TextInput } from '@mantine/core';
import {
  IconAlertCircle,
  IconClock,
  IconInbox,
  IconPencil,
  IconSearch,
  IconSend,
  IconTrash,
  IconX,
} from '@tabler/icons';
import { FC, useContext, useEffect, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from '@mantine/hooks';
import NavigationLink from '../../Atoms/Navigation/NavigationLink';
import { useNavigationItem } from '../../Organismns/Navigation/NavigationItemHook';
import { WindowContext } from '../../Service/Context/WindowContext';
import { PostboxCountersContext } from '../../Service/Context/PostboxCountersContext';
import { ThemeTitle } from '../../Atoms/ThemeText';
import { EmailBoxContext } from '../../Service/Context/EmailBoxContext';
import InboxIcon from '../../Atoms/InboxIcon';
import EmailBoxCounter from './EmailBoxCounter';
import MailOutboundConfigNav from './imap/MailOutboundConfigNav';

const EmailInboxNavLinks: FC<{ closeNavBar: () => void }> = ({ closeNavBar }) => {
  const navigate: NavigateFunction = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { sm } = useContext(WindowContext);
  const { isBookitupScope, openFolder, searchQuery, loading } = useContext(EmailBoxContext);
  const { unreadCount, failedCount, scheduledCount } = useContext(PostboxCountersContext);
  const [localQuery, setLocalQuery] = useState(searchQuery ?? '');
  const [debouncedQuery] = useDebouncedValue(localQuery, 500);

  useEffect(() => {
    if (debouncedQuery !== (searchQuery ?? '')) {
      const nextSearchParams = new URLSearchParams(searchParams);
      if (debouncedQuery) {
        nextSearchParams.set('searchQuery', debouncedQuery);
      } else {
        nextSearchParams.delete('searchQuery');
      }
      setSearchParams(nextSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  useEffect(() => {
    setLocalQuery(searchQuery ?? '');
  }, [searchQuery]);

  const clearSearchQuery = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('searchQuery');
    setLocalQuery('');
    setSearchParams(nextSearchParams, { replace: true });
  };

  const _openFolder = (name: string, link: string, totalMessages?: number | null) => {
    openFolder({
      name,
      src: 'BOOKITUP',
      totalMessages,
    });
    navigate(searchQuery ? `${link}?searchQuery=${encodeURIComponent(searchQuery)}` : link);
    closeNavBar();
  };

  return (
    <>
      {!sm && (
        <ThemeTitle p="md" order={3}>
          Postfach
        </ThemeTitle>
      )}
      <Box p="xs" mb={5}>
        <TextInput
          value={localQuery}
          onChange={(e) => setLocalQuery(e.currentTarget.value)}
          icon={<IconSearch size={16} />}
          placeholder="E-mails durchsuchen..."
          variant="filled"
          disabled={loading && !isBookitupScope}
          rightSection={
            loading ? (
              <Loader size="xs" />
            ) : (
              localQuery && (
                <ActionIcon aria-label="Suchfilter löschen" onClick={clearSearchQuery}>
                  <IconX size={16} />
                </ActionIcon>
              )
            )
          }
        />
      </Box>
      <NavigationLink
        defaultOpened
        compact
        label={<Text weight="bolder">bookitup</Text>}
        icon={<InboxIcon size={20} />}
      >
        <NavigationLink
          label="Posteingang"
          compact
          active={pathname === '/postbox/inbox' && isBookitupScope}
          onClick={() => _openFolder('inbox', '/postbox/inbox')}
          variant="filled"
        />
        <NavigationLink
          label="Unerledigt"
          compact
          active={useNavigationItem('/postbox/inbox/unread') && isBookitupScope}
          onClick={() => _openFolder('Unerledigt', '/postbox/inbox/unread', unreadCount)}
          variant="filled"
          rightSection={<EmailBoxCounter count={unreadCount} />}
        />
        <NavigationLink
          label="ohne Eventbezug"
          compact
          active={useNavigationItem('/postbox/inbox/unassigned') && isBookitupScope}
          onClick={() => _openFolder('ohne Eventbezug', '/postbox/inbox/unassigned', null)}
          variant="filled"
        />
        <NavigationLink
          label="Gesendet"
          compact
          icon={sm && <IconSend size={16} />}
          active={useNavigationItem('/postbox/outbound') && isBookitupScope}
          onClick={() => _openFolder('Gesendet', '/postbox/outbound')}
          variant="filled"
        />
        <NavigationLink
          label="Entwürfe"
          compact
          icon={sm && <IconPencil size={16} />}
          active={useNavigationItem('/postbox/drafts') && isBookitupScope}
          onClick={() => _openFolder('Entwürfe', '/postbox/drafts', null)}
          variant="filled"
        />
        <NavigationLink
          label="Geplant"
          compact
          icon={sm && <IconClock size={16} />}
          active={useNavigationItem('/postbox/scheduled') && isBookitupScope}
          onClick={() => _openFolder('Geplant', '/postbox/scheduled', null)}
          variant="filled"
          rightSection={<EmailBoxCounter count={scheduledCount} />}
        />
        <NavigationLink
          label="Fehlgeschlagen"
          compact
          icon={sm && <IconAlertCircle size={16} />}
          active={useNavigationItem('/postbox/failed') && isBookitupScope}
          onClick={() => _openFolder('Fehlgeschlagen', '/postbox/failed', failedCount)}
          variant="filled"
          rightSection={<EmailBoxCounter count={failedCount} />}
        />
        <NavigationLink
          label="Papierkorb"
          compact
          icon={sm && <IconTrash size={16} />}
          active={useNavigationItem('/postbox/trash') && isBookitupScope}
          onClick={() => _openFolder('Papierkorb', '/postbox/trash', null)}
          variant="filled"
        />
      </NavigationLink>
      <MailOutboundConfigNav closeNavBar={closeNavBar} />
    </>
  );
};

export default EmailInboxNavLinks;
